import 'leaflet/dist/leaflet.css';
import { createContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LayersControl, TileLayer, useMap } from 'react-leaflet';

import { Spinner } from 'components/ui';

import { MarkerClusterGroupLayer } from '../MarkerClusterGroupLayer';

import { StyledMap as _StyledMap, StyledSelect } from './Map.style';

export const WORLD_BOUNDS = [
  [85, -180],
  [-85, 180],
];

const SATELLITE_BASE_MAP = 'World_Imagery';
const STREET_BASE_MAP = 'World_Street_Map';

/** True when the map basemap is satellite imagery (labels/icons need light contrast). */
export const MapSatelliteContext = createContext(false);

function BaseMapToggle({ onChange, value }) {
  const { t } = useTranslation();
  return (
    <StyledSelect
      options={[
        { label: t('components.satelliteView', 'Satellite View'), value: SATELLITE_BASE_MAP },
        { label: t('components.streetView', 'Street View'), value: STREET_BASE_MAP },
      ]}
      value={value}
      onChange={onChange}
    />
  );
}

function SatelliteViewLayerControl({ checked, onChange }) {
  const { t } = useTranslation();
  const map = useMap();
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const mapContainer = map.getContainer();

    // Leaflet's layers control empties its overlays list on every rebuild, which detaches
    // this node. Reusing one node keeps re-attaching to a DOM move, instead of a new
    // element plus a state update and portal remount each time.
    const mountPoint = document.createElement('div');
    mountPoint.setAttribute('data-satellite-view-layer', 'true');

    const ensureAttached = () => {
      const overlays = mapContainer.querySelector('.leaflet-control-layers-overlays');
      if (overlays && mountPoint.parentNode !== overlays) {
        overlays.appendChild(mountPoint);
      }
    };

    ensureAttached();
    setContainer(mountPoint);

    const observerTarget = mapContainer.querySelector('.leaflet-control-container') ?? mapContainer;
    const observer = new MutationObserver(ensureAttached);
    observer.observe(observerTarget, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mountPoint.remove();
      setContainer(null);
    };
  }, [map]);

  if (!container) {
    return null;
  }

  return createPortal(
    <label className="leaflet-control-layers-selector">
      <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />
      <span> {t('components.satelliteView', 'Satellite View')}</span>
    </label>,
    container
  );
}

export const StyledMap = function Leaflet({
  mapRef,
  setMapRef = () => {},
  mapRefReady,
  setMapRefReady = () => {},
  // Required bounds format: [[maxLat, minLng], [minLat, maxLng]]
  // If no bounds provided, use world bounds
  bounds,
  children,
  enableClustering,
  spiderfy = false,
  satelliteView,
  toggleBaseMap,
  sideList,
  // if true, expose layers control and include layerOverlays
  layersControl,
  height,
  ...props
}) {
  const { t } = useTranslation();
  const server = satelliteView ? SATELLITE_BASE_MAP : STREET_BASE_MAP;
  const [baseMap, setBaseMap] = useState(server);

  // In the fleet map where we use the layersControl, each child is a layer
  // In other cases, each child is a point
  const [turbineLayer, labelsLayer] = layersControl ? children : [[], []];

  // if we're not on the sideList for create asset preview, then just set the bounds
  // to the current bounds of all turbines on the map.
  useEffect(() => {
    if (mapRef && !sideList && bounds && mapRefReady) {
      mapRef.fitBounds(bounds);
    }
  }, [mapRef, bounds, mapRefReady, sideList]);

  const isSatelliteView = baseMap === SATELLITE_BASE_MAP;

  return (
    <>
      {!mapRefReady && <Spinner />}
      <MapSatelliteContext.Provider value={isSatelliteView}>
        <_StyledMap
          bounds={WORLD_BOUNDS}
          maxBounds={WORLD_BOUNDS}
          zoomDelta={0.25}
          zoomSnap={0}
          minZoom={1}
          maxZoom={23}
          // whenCreated prop fires after the map is created and we need a way to set the ref.
          // the old way on v2 doesn't work so you need to set ref after map is created.
          // https://github.com/PaulLeCam/react-leaflet/issues/841
          whenCreated={mapRef => {
            setMapRef(mapRef);
            // Setting mapRefReady prevents a race condition that causes the map to crash
            // when trying to set the initial map bounds for the first item in mapList
            setMapRefReady(true);
          }}
          $height={height}
          {...props}
        >
          <TileLayer
            attribution={`Tiles © <a href="https://services.arcgisonline.com/arcgis/rest/services">ArcGIS</a>`}
            url={`https://server.arcgisonline.com/ArcGIS/rest/services/${baseMap}/MapServer/tile/{z}/{y}/{x}.png`}
            // passing a key forces the map to re-render when toggling between street and satellite view
            key={baseMap}
            maxNativeZoom={19}
            maxZoom={23}
          />

          {layersControl ? (
            <LayersControl position="bottomleft" collapsed={false}>
              {/* Checked Setting enables the turbine base layer by default */}
              <LayersControl.Overlay checked name={t('table.turbines', 'Turbines')}>
                <MarkerClusterGroupLayer
                  layer={turbineLayer}
                  spiderfy={spiderfy}
                  enableClustering={enableClustering}
                />
              </LayersControl.Overlay>

              <LayersControl.Overlay name={t('components.turbineLabels', 'Turbine Labels')}>
                <MarkerClusterGroupLayer
                  layer={labelsLayer}
                  spiderfy={spiderfy}
                  enableClustering={enableClustering}
                />
              </LayersControl.Overlay>
            </LayersControl>
          ) : (
            <MarkerClusterGroupLayer
              layer={children}
              spiderfy={spiderfy}
              enableClustering={enableClustering}
            />
          )}

          {layersControl && (
            <SatelliteViewLayerControl
              checked={isSatelliteView}
              onChange={checked => setBaseMap(checked ? SATELLITE_BASE_MAP : STREET_BASE_MAP)}
            />
          )}

          {toggleBaseMap && <BaseMapToggle onChange={setBaseMap} value={baseMap} />}
        </_StyledMap>
      </MapSatelliteContext.Provider>
    </>
  );
};
